import aviso from '../lib/aviso';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Interface estendida para incluir propriedade de controle de retry
 */
interface RequestInitRetry extends RequestInit {
    _retry?: boolean;
}

const urlDaRequisicao = (input: RequestInfo | URL): string =>
    (typeof input === 'string' ? input : input instanceof URL ? input.href : input.url).toLowerCase();

// Endpoints que precisam continuar funcionando mesmo com assinatura vencida
// (o usuário precisa conseguir renovar o plano)
const SUBSCRIPTION_ENDPOINT_PATTERNS = ['/api/assinatura', '/api/auth'];

const isSubscriptionExemptEndpoint = (input: RequestInfo | URL): boolean => {
    const url = urlDaRequisicao(input);
    return SUBSCRIPTION_ENDPOINT_PATTERNS.some(pattern => url.includes(pattern));
};

// Um 401 nestes endpoints é resposta legítima (credencial errada, sessão que
// acabou mesmo), não access token vencido: renovar aqui só criaria laço.
const ENDPOINTS_SEM_RENOVACAO = [
    '/api/auth/login',
    '/api/auth/refresh-token',
    '/api/auth/logout',
    '/api/auth/register',
    '/api/auth/confirmaremail',
    '/api/auth/solicitarredefinicao',
    '/api/auth/redefinirsenha',
    '/api/auth/convite/',
    '/api/auth/aceitarconvite',
];

const dispensaRenovacao = (input: RequestInfo | URL): boolean => {
    const url = urlDaRequisicao(input);
    return ENDPOINTS_SEM_RENOVACAO.some(pattern => url.includes(pattern));
};

const isMutatingRequest = (init?: RequestInitRetry): boolean => {
    const method = (init?.method || 'GET').toUpperCase();
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
};

const isAssinaturaVencida = (): boolean => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user?.assinaturaVencida === true;
    } catch {
        return false;
    }
};

// Disparado para que o MainLayout abra a modal de assinatura vencida de qualquer lugar do app
export const ASSINATURA_VENCIDA_EVENT = 'lemeia:assinatura-vencida-bloqueio';

/* A API recusou porque a funcionalidade foi desligada para a empresa
   (Sistema › Funcionalidades). É diferente do 403 de permissão, e o `codigo`
   no corpo é o que separa os dois. O MainLayout escuta: relê o Auth/Me e tira
   a pessoa da tela. A resposta segue intacta para quem chamou tratar. */
export const FUNCIONALIDADE_DESLIGADA_EVENT = 'lemeia:funcionalidade-desligada';

/* A API recusou por falta de chave do catálogo de permissões. Diferente da
   funcionalidade desligada: não desloga nem relê o Auth/Me, só avisa. O corpo
   traz as chaves exigidas em `permissoes`. */
export const PERMISSAO_NEGADA_EVENT = 'lemeia:permissao-negada';

const avisarSeRecusado = (response: Response) => {
    if (response.status !== 403) return;
    response.clone().json()
        .then(corpo => {
            if (corpo?.codigo === 'FUNCIONALIDADE_DESLIGADA') {
                window.dispatchEvent(new CustomEvent(FUNCIONALIDADE_DESLIGADA_EVENT, { detail: corpo.funcionalidade }));
            } else if (corpo?.codigo === 'PERMISSAO_NEGADA') {
                window.dispatchEvent(new CustomEvent(PERMISSAO_NEGADA_EVENT, { detail: corpo.permissoes ?? [] }));
            }
        })
        .catch(() => { /* 403 sem corpo JSON */ });
};

/** A sessão acabou de verdade: o refresh token venceu, foi invalidado ou sumiu. */
class SessaoExpiradaError extends Error {
    constructor() {
        super('Sessão expirada');
        this.name = 'SessaoExpiradaError';
    }
}

/*
 * Uma renovação de cada vez.
 *
 * Quando o access token vence, várias chamadas levam 401 no mesmo instante: os
 * polls de notificação (15s e 60s), o que a tela aberta estiver carregando, o
 * que o usuário acabou de clicar. Sem esta trava cada uma dispara seu próprio
 * POST /refresh-token. O refresh token é de uso único na API, então a primeira
 * ganha e rotaciona; as outras chegam com o token já consumido, tomam
 * "Refresh token already used" e a API ainda limpa os cookies — apagando a
 * sessão que a primeira tinha acabado de renovar. Resultado: o usuário cai no
 * login mesmo estando ativo. Com a trava, quem chega depois espera a mesma
 * promessa e repete a chamada original com os cookies já novos.
 */
let renovacaoEmAndamento: Promise<void> | null = null;
let sessaoJaEncerrada = false;

const renovarSessao = (): Promise<void> => {
    if (!renovacaoEmAndamento) {
        renovacaoEmAndamento = fetch(`${API_URL}/api/Auth/refresh-token`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(res => {
                if (res.ok) return;
                // Só o 401 quer dizer "acabou a sessão". 429 (rate limit),
                // 500 ou queda de rede são falhas passageiras e não podem
                // deslogar ninguém.
                if (res.status === 401) throw new SessaoExpiradaError();
                throw new Error(`Falha temporária ao renovar a sessão (HTTP ${res.status})`);
            })
            .finally(() => {
                renovacaoEmAndamento = null;
            });
    }

    return renovacaoEmAndamento;
};

const encerrarSessao = () => {
    if (sessaoJaEncerrada) return;
    sessaoJaEncerrada = true;

    localStorage.removeItem('user');

    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

/**
 * Wrapper personalizado do fetch para lidar com:
 * 1. Interceptação de respostas 401 (Não Autorizado)
 * 2. Tentativa de refresh token automotática
 * 3. Retry da requisição original
 * 4. Bloqueio de ações de criação/edição quando a assinatura está vencida
 */
export const apiFetch = async (input: RequestInfo | URL, init?: RequestInitRetry): Promise<Response> => {
    if (isMutatingRequest(init) && !isSubscriptionExemptEndpoint(input) && isAssinaturaVencida()) {
        window.dispatchEvent(new CustomEvent(ASSINATURA_VENCIDA_EVENT));
        // Limpa qualquer toast genérico de erro que a página de origem dispare
        // ao capturar a rejeição abaixo, já que o aviso real é mostrado na modal.
        setTimeout(() => aviso.fechar(), 50);
        return Promise.reject(new Error('Assinatura vencida'));
    }

    // Garante que credentials: 'include' esteja presente para enviar cookies
    const config: RequestInitRetry = {
        ...init,
        credentials: 'include',
    };

    /* Aba escondida: os avisos e a pipeline seguem perguntando ao servidor, e a API conta cada
       chamada como uso para o "Último acesso" de Sistema › Visão geral. Este cabeçalho diz que
       ninguém está olhando, senão uma aba esquecida aberta faria a pessoa parecer ativa a noite toda
       (UltimoAcessoMiddleware, na API). */
    if (document.visibilityState === 'hidden') {
        const headers = new Headers(init?.headers);
        headers.set('X-Brik-Segundo-Plano', '1');
        config.headers = headers;
    }

    try {
        // Tenta a requisição original
        const response = await fetch(input, config);
        avisarSeRecusado(response);

        if (response.status !== 401 || config._retry || dispensaRenovacao(input)) {
            return response;
        }

        config._retry = true;

        try {
            await renovarSessao();
        } catch (erroRenovacao) {
            if (erroRenovacao instanceof SessaoExpiradaError) {
                encerrarSessao();
                return Promise.reject(erroRenovacao);
            }

            // Falha passageira: devolve o 401 original para a tela tratar em vez
            // de derrubar a sessão. A próxima chamada tenta renovar de novo.
            console.warn('Não foi possível renovar a sessão agora:', erroRenovacao);
            return response;
        }

        // Renovou: repete a chamada original, agora com os cookies novos
        const repetida = await fetch(input, config);
        avisarSeRecusado(repetida);
        return repetida;
    } catch (error) {
        return Promise.reject(error);
    }
};
